import React, { useState, useEffect, useRef, useMemo } from "react";
import { observer } from "mobx-react";
import makeApi, { URLS } from "Utils/apiURL";
import deepResearchStore from "Stores/deepResearchData";

// Research Assistant chat. Conversation history lives in
// deepResearchStore.chatHistory (rehydrated per contact on overview load),
// so the SAME conversation is shared by the Deep Research side panel and the
// Contact-page tab's slide-in assistant.
// The backend occasionally leaks a JSON-shaped string (truncated or fence-
// wrapped LLM output) into the text field. Never render raw braces: detect a
// JSON-ish payload, pull out the subject/body/text/script fields, and fall
// back to a de-scaffolded plain-text version when it won't parse.
const normalizeAiResponse = data => {
  const out = {
    type: data?.type || "text",
    subject: data?.subject || "",
    body: data?.body || "",
    text: data?.text || ""
  };
  let raw = typeof out.text === "string" ? out.text.trim() : "";
  if (raw.startsWith("```")) {
    raw = raw.replace(/^```[a-zA-Z]*\s*/, "").replace(/```\s*$/, "").trim();
  }
  if (!raw.startsWith("{")) {
    out.text = raw || out.text;
    return out;
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      if (parsed.subject && parsed.body) {
        return {
          type: "email",
          subject: String(parsed.subject),
          body: String(parsed.body),
          text: ""
        };
      }
      const inner = parsed.text || parsed.script || parsed.body || parsed.message;
      if (inner) {
        return { ...out, type: "text", text: String(inner) };
      }
    }
  } catch (e) {
    // Truncated JSON — salvage the longest recognizable field, else strip
    // the JSON scaffolding so the user sees readable text.
    const field = key => {
      const m = raw.match(new RegExp(`"${key}"\\s*:\\s*"([\\s\\S]*?)(?:"\\s*[,}]|$)`));
      return m ? m[1].replace(/\\n/g, "\n").replace(/\\"/g, '"') : "";
    };
    const subject = field("subject");
    const body = field("body") || field("text") || field("script");
    if (subject && body) {
      return { type: "email", subject, body, text: "" };
    }
    if (body) {
      return { ...out, type: "text", text: body };
    }
    const plain = raw
      .replace(/[{}[\]"]/g, " ")
      .replace(/\\n/g, "\n")
      .replace(/[ \t]+/g, " ")
      .trim();
    return { ...out, type: "text", text: plain || raw };
  }
  return out;
};

const ChatPanel = observer(({ contactName, contactId }) => {
  // Hydrate from the store's chatHistory (rehydrated by setProfileResult on overview load)
  const conversations = deepResearchStore.chatHistory;
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [previousId, setPreviousId] = useState("");
  const endRef = useRef(null);

  const quickActions = useMemo(() => {
    const who = contactName || "this prospect";
    return [
      { label: "Cold Email", icon: "📧", query: `Write a personalized cold email to ${who}` },
      { label: "LinkedIn DM", icon: "💬", query: `Write a LinkedIn message to ${who}` },
      { label: "Cold Call Script", icon: "📞", query: `Write a cold call script for ${who}` },
      { label: "Follow-up Email", icon: "📨", query: `Write a follow-up email to ${who}` }
    ];
  }, [contactName]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth" });
  }, [conversations.length, sending]);

  const send = (query) => {
    if (!query || !query.trim() || sending) return;

    const userMsg = { role: "user", content: query, ts: Date.now() };
    deepResearchStore.appendChatMessage(userMsg);
    const nextHistory = [...conversations, userMsg];
    setInput("");
    setSending(true);

    const profile = deepResearchStore.profileData || {};
    makeApi({
      url: URLS.deepResearchUserProfile.replace("/userProfile", "/getResponseFromAI"),
      method: "POST",
      data: {
        query_type: "general",
        query,
        contactId: contactId || "",
        // Rich grounding context — the backend prompt asks the model to cite from these.
        prospectInsights: deepResearchStore.structuredProfile || {},
        companyInsights: deepResearchStore.companyInsights || {},
        sellingSignals: deepResearchStore.sellingSignals || [],
        conversationStarters: deepResearchStore.conversationStarters || [],
        keyMentions: deepResearchStore.insightsData?.key_mentions || [],
        companyData: deepResearchStore.companyData || {},
        deepDiveIntel: deepResearchStore.deepDiveIntel || {},
        sellerProfile: deepResearchStore.activeSellerProfile || {},
        // Source URLs + snippets so the model can answer "give me the link" / "tell me more about the post".
        serpLinks: deepResearchStore.serpLinks || {},
        profileData: {
          firstName: profile.firstName,
          lastName: profile.lastName,
          subTitle: profile.subTitle,
          company: profile?.position?.[0]?.company?.name
        },
        previousId: previousId || "",
        previousChats: nextHistory.map(c => ({
          type: c.role === "user" ? "user" : "ai",
          content: c.content,
          ...(c.subject ? { subject: c.subject, body: c.body } : {})
        }))
      }
    }).then(res => {
      const data = res?.data;
      if (data) {
        const normalized = normalizeAiResponse(data);
        const aiMsg = {
          role: "ai",
          type: normalized.type,
          content: normalized.text || normalized.body || "",
          subject: normalized.subject,
          body: normalized.body,
          ts: Date.now()
        };
        if (data.response_id) setPreviousId(data.response_id);
        deepResearchStore.appendChatMessage(aiMsg);
      }
      setSending(false);
    }).catch(() => setSending(false));
  };

  return (
    <div className="drChatPanel">
      <div className="drChatHeader">
        <span className="drChatTitle">Research Assistant</span>
        <span className="drChatHint">Chat about {contactName || "this prospect"}</span>
      </div>
      {conversations.length === 0 && (
        <div className="drChatQuickActions">
          {quickActions.map((a, i) => (
            <button key={i} className="drChatQuickAction" onClick={() => send(a.query)}>
              <span>{a.icon}</span> <span>{a.label}</span>
            </button>
          ))}
        </div>
      )}
      <div className="drChatMessages">
        {conversations.map((msg, i) => (
          <div key={i} className={`drChatMsg drChatMsg-${msg.role}`}>
            <div className="drChatMsgAvatar">{msg.role === "user" ? "👤" : "🤖"}</div>
            <div className="drChatMsgContent">
              {msg.type === "email" ? (
                <div className="drChatEmail">
                  {msg.subject && <div className="drChatEmailSubject">Subject: {msg.subject}</div>}
                  <div className="drChatEmailBody">{msg.body || msg.content}</div>
                </div>
              ) : (
                <div className="drChatText">{msg.content}</div>
              )}
            </div>
          </div>
        ))}
        {sending && (
          <div className="drChatMsg drChatMsg-ai">
            <div className="drChatMsgAvatar">🤖</div>
            <div className="drChatMsgContent">
              <div className="drChatTyping"><span>.</span><span>.</span><span>.</span></div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      <div className="drChatInput">
        <input
          type="text"
          placeholder="Ask anything..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") send(input); }}
          disabled={sending}
        />
        <button onClick={() => send(input)} disabled={sending || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
});

export default ChatPanel;
